<template>
  <div>
    <template v-if="productAmount > 0">
      <div class="grid-item grid-item-data cart-item">
        <div class="articul">
          <a class="hide-desktop fav-code" href="#" @click.prevent="switchFavourite">
            <i class="svg-icon star" :class="{ 'star-favourite': isFavourite }" />
          </a>

          <span>{{ product.code }}</span>

          <div class="hide-desktop fav-code del-item">
            <a class="circle" href="#" @click.prevent="deleteCart()">
              <i class="svg-icon cross-little icon-pointer" />
            </a>
          </div>
        </div>

        <div class="brand">
          <div class="title-col hide-desktop">{{ $t('brand') }}</div>
          <div>{{ product.brand_name }}</div>
        </div>

        <div class="name">
          <nuxt-link
            class="link-ext"
            :to="localePath('/product/' + product.slug)"
          >
            {{ productLocaleData.name }}
          </nuxt-link>
        </div>

        <div class="store store_name">
          <div class="title-col hide-desktop">{{ $t('stock') }}</div>
          <div
            v-for="stock in stocks.filter(x => (product.amount_cart[x.stock_type] > 0) && x.stock_type !== 'supplier')"
            :class="[`stock-${stock.stock_type}`, { 'pointer': stock.stock_type !== 'available' }]"
            @mouseover="showTooltip[stock.stock_type] = !showTooltip[stock.stock_type] && (['preorder', 'transit',].includes(stock.stock_type))"
            @mouseleave="showTooltip[stock.stock_type] = false"
          >
            <template v-if="stock.stock_type === 'preorder'">{{ $t('remote') }}</template>
            <template v-else>{{ $t(`stock_type_${stock.stock_type}`) }}</template>

            <Transition name="fade">
              <div
                v-if="showTooltip[stock.stock_type] && shipmentDays(stock.stock_type) > 0 && (['preorder',].includes(stock.stock_type))"
                class="tooltip"
              >
                <span>
                  {{ deliveryDeadline(stock.stock_type) }}
                  {{ shipmentDays(stock.stock_type) }}
                  {{ shipmentDays(stock.stock_type) | ending([$t('day_1'), $t('day_2'), $t('day_3')], $i18n.locale) }}
                </span>
              </div>
            </Transition>
            <Transition name="fade">
              <div 
                v-show="showTooltip[stock.stock_type] && stock.stock_type === 'transit'"
                class="tooltip tooltip-cart"
              >
                <div>{{ product.amount_free.transit | integerPart }} {{ $t('pcs_transit') }}</div>
                <template v-if="product.shipment_date">
                  {{ $t('expected_shipment_date') }}: {{ product.shipment_date }}
                </template>
                <span v-else>
                  {{ deliveryDeadline('transit') }}
                  {{ shipmentDays('transit') }}
                  {{ shipmentDays('transit') | ending([$t('day_1'), $t('day_2'), $t('day_3')], $i18n.locale) }}
                </span>
              </div>
            </transition>
          </div>
        </div>

        <div class="store store_qnt">
          <div class="title-col hide-desktop">{{ $t('store_pcs') }}</div>
          <div
            :class="`stock-${stock.stock_type}`"
            v-for="stock in stocks.filter(x => (product.amount_cart[x.stock_type] > 0 && x.stock_type !== 'supplier'))"
          >
            <template v-if="stock.stock_type === 'preorder'">
              {{ $t(`stock_type_${stock.stock_type}`) }}
            </template>
            <template v-else>
              {{ product.amount_stock[stock.stock_type] | integerPart }}
            </template>
          </div>
        </div>

        <div class="price">
          <div class="title-col hide-desktop">{{ $t('price_rub') }}</div>
          <div
            :class="`stock-${stock.stock_type}`"
            v-for="stock in stocks.filter(x => (product.amount_cart[x.stock_type] > 0) && x.stock_type !== 'supplier')"
          >
            <template v-if="productAmount >= product.discount_min_qnt && getDiscountRate(product) !== 1">
              <div class="price-discounted">
                {{ getPrice(product) | integerPart }}
                <span class="small">{{ getPrice(product) | fractionalPart }}</span>
              </div>
              <div class="price-old">
                <span class="price-old_value">
                  {{ product.price | integerPart }}<span class="small">{{ product.price | fractionalPart }}</span>
                </span>
                <span class="price-old_discount">
                  {{ -1 * product.discount_value }}%
                </span>
              </div>
            </template>
            <template v-else>
              {{ product.price | integerPart }}<span class="small">{{ product.price | fractionalPart }}</span>
            </template>
          </div>
        </div>

        <div class="order order-qnt-edit">
          <div class="link-tooltip">
            <a href="#" @click.prevent="decreaseAmount"><i class="svg-icon minus icon-pointer" /></a>
            <input-number v-model="amountCart" :value="amountCart" :min-value="0" />
            <a href="#" @click.prevent="increaseAmount"><i class="svg-icon plus icon-pointer" /></a>
            <a class="button-ok" :class="{ 'hidden': amountCart === productAmount }" href="#" @click.prevent="updateAmount">
              OK
            </a>

            <transition name="fade">
              <div v-show="showTooltipQnt" class="tooltip">
                <site-text-block type="warn_qnt_preorder" class="warn_qnt_preorder" />
                <a @click="showTooltipQnt = false" class="close-tooltip">
                  <i class="svg-icon cross-little icon-pointer" /></a>
              </div>
            </transition>
          </div>
        </div>

        <div class="total">
          <div class="title-col hide-desktop">{{ $t('sum_rub') }}</div>

          <span class="text-align-center">
            {{ totalPrice | integerPart }}<span class="small">{{ totalPrice | fractionalPart }}</span>
          </span>

          <div class="hide-phone del-item text-align-right">
            <a class="circle" href="#" @click.prevent="deleteCart()">
              <i class="svg-icon cross-little icon-pointer" /></a>
          </div>
        </div>

        <div
          class="discount-item increase-qnt"
          v-if="product.discount_value > 0 && product.discount_min_qnt > productAmount"
        >
          <span>
            {{ $t('to_apply_discount').replace(
              '{discount}', product.discount_value
            ).replace(
              '{min_qnt}', product.discount_min_qnt
            ) }}
          </span>
          <span class="popup-container" :class="{ 'active': discountDescrVisible }">
            <i class="svg-icon info" @click="discountDescrVisible = !discountDescrVisible" v-show="!discountDescrVisible"/>
            <i class="svg-icon cross-little" @click="discountDescrVisible = !discountDescrVisible" v-show="discountDescrVisible"/>
            <div class="popup-content" v-show="discountDescrVisible">
              <site-text-block type="discount_descr" class="discount_descr" />
            </div>
          </span>
        </div>
      </div>
      <!-- <div class="grid-item grid-item-data- discount-item" v-if="product.discount_value > 0">
        {{ product.discount_min_qnt }} - {{ product.discount_value }}
      </div> -->
    </template>
  </div>
</template>

<script>
import { ProductFavourite } from "@/models/product";

import InputNumber from '@/components/controls/input-number';
import SiteTextBlock from '@/components/blocks/site-text-block';

const TOOLTIP_TIMEOUT = 5000;

export default {
  components: {
    InputNumber,
    SiteTextBlock,
  },
  props: [
    'product',
    'stocks'
  ],
  data() {
    return {
      amountStock: 0,
      amountCart: 0,
      showDeleteDialog: false,
      isFavourite: false,
      showTooltip: { transit: false, preorder: false },
      showTooltipQnt: false,
      timerTooltopQnt: null,
      enableTooltipQnt: true,
      discountDescrVisible: false,
      discountDescrTimer: null,
    }
  },
  watch: {
    'product.amount_cart'(value) {
      this.amountCart = Object.values(value).reduce((a, b) => a + b, 0);
    },
    'discountDescrVisible'(value) {
      if (value) {
        this.discountDescrTimer = setTimeout(() => this.discountDescrVisible = false, TOOLTIP_TIMEOUT)
      } else {
        clearTimeout(this.discountDescrTimer);
      }
    },
  },
  computed: {
    productLocaleData() {
      return this.product?.product_translations?.find(x => x.language.slug === this.$i18n.locale) || {};
    },
    productAmount() {
      return Object.values(this.product.amount_cart).reduce((a, b) => a + b, 0);
    },
    totalPrice() {
      let sum = this.productAmount * this.getPrice(this.product);
      return sum;
    },
  },
  created() {
    this.amountCart = this.productAmount;
    this.isFavourite = this.product.is_favourite;
    this.amountStock = Object.values(this.product.amount_stock).reduce((a, b) => a + b, 0)
    this.enableTooltipQnt = this.amountStock >= this.amountCart
  },
  methods: {
    getPrice(product) {
      let res = product.price;
      if(this.getDiscountRate(product) !== 1){
        res = parseInt(res * this.getDiscountRate(product));
      }
      return res;
    },
    getDiscountRate(product) {
      let rate = 1;
      if (this.productAmount >= product.discount_min_qnt) {
        return (100 - product.discount_value) / 100;
      }
      return rate;
    },
    checkPreorderQnt() {
      if (this.enableTooltipQnt) {
        this.showTooltipQnt = this.amountCart > this.amountStock
        clearTimeout(this.timerTooltopQnt);
        this.timerTooltopQnt = setTimeout(() => this.showTooltipQnt = false, TOOLTIP_TIMEOUT)
      }
    },
    decreaseAmount() {
      if (this.amountCart > this.product.package_quantity) {
        let amount = this.amountCart - this.product.package_quantity;
        if (amount % this.product.package_quantity) {
          amount += this.product.package_quantity - amount % this.product.package_quantity;
        }
        this.amountCart = amount;
        // this.updateAmount();
      }
      this.checkPreorderQnt();
    },
    increaseAmount() {
      let amount = this.amountCart + this.product.package_quantity;
      amount -= amount % this.product.package_quantity;
      this.amountCart = amount;
      // this.updateAmount();
      this.checkPreorderQnt();
    },
    updateAmount() {
      const data = {
        product: this.product.id,
        amount: this.amountCart,
        replace_cart: 1,
      };
      // this.$axios.$post('/order/add-cart/', data).then((response) => {
      this.$axios.$post('/order/update-cart/', data).then((response) => {
        this.$emit('fetch-cart');
        this.showTooltipQnt = false;
        this.enableTooltipQnt = this.amountStock >= this.amountCart;
      }).catch((e) => {
        this.amountCart = this.productAmount;
        try {
          this.$notify({
            duration: 5000,
            text: e.response.data.detail || e.response.data.amount[0],
            ignoreDuplicates: true,
          });
        } catch (e) {
        }
      });
    },
    deleteCart() {
      const data = {
        product: this.product.id,
      };
      this.$axios.$post('/order/user-order/cart-delete/', data).then((response) => {
        this.$store.dispatch('setCartExists');
        this.$emit('fetch-cart');
      }).catch((e) => {
        try {
          this.$notify({
            duration: 5000,
            text: e.response.data.detail,
            ignoreDuplicates: true,
          });
        } catch (e) {
        }
      }).finally(() => {
        this.showDeleteDialog = false;
      });
    },
    switchFavourite() {
      if (this.$auth.loggedIn) {
        let fav = new ProductFavourite({ product: this.product.id });
        if (this.isFavourite) {
          fav.id = this.product.id;
          fav.delete().then(() => {
            this.$store.dispatch('setFavouriteProductsExists');
            this.isFavourite = false;
            this.$notify({
              duration: 1000,
              text: this.$t('product_removed_from_fav'),
            });
            this.$emit('remove-favourite');
          });
        } else {
          fav.save().then(() => {
            this.$store.dispatch('setFavouriteProductsExists');
            this.isFavourite = true;
            this.$notify({
              duration: 1000,
              text:
                `${this.$t('product_added_to_fav_1')} <a href="${this.localePath('/profile/orders/')}">${this.$t('product_added_to_fav_2')}</a>`,
            });
          });
        }
      }
    },
    deliveryDeadline(stockType) {
      const dl = this.stocks.find(x => x.stock_type === stockType)?.shipment_days_limit;
      if (dl === 'gt') {
        return this.$t('delivery_deadline_from')
      }
      else if (dl === 'lt') {
        return this.$t('delivery_deadline_to')
      }
      return this.$t('delivery_deadline')
    },
    shipmentDays(stockType) {
      // if(stockType === 'transit') stockType = 'available';
      return this.stocks.find(x => x.stock_type === stockType)?.shipment_days;
    },
  },
}
</script>

<i18n lang="yaml">
ru:
  preorder: 'под заказ'
  quantity_pcs: 'Количество шт.'
  delete_product_confirm_1: 'Вы уверены, что хотите удалить этот товар?'
  delete_product_confirm_2: 'Подтвердите действие!'
  delete: 'Удалить'
  product_added_to_fav_1: 'Товар добавлен в избранное. В избранное можно перейти в'
  product_added_to_fav_2: 'личном кабинете'
  product_removed_from_fav: 'Товар удален из избранного.'
  remote: 'Производство'
en:
  preorder: 'preorder'
  quantity_pcs: 'Quantity, pcs.'
  delete_product_confirm_1: 'Are you sure you want to delete this product?'
  delete_product_confirm_2: 'Confirm action!'
  delete: 'Delete'
  product_added_to_fav_1: 'Product is added to favourites. You can view favourite products in'
  product_added_to_fav_2: 'personal account'
  product_removed_from_fav: 'Product is removed from favourites.'
  remote: 'Remote stock'
</i18n>
